import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToHorizontalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconCrossSmall } from 'central-icons/IconCrossSmall';
import { IconPlusSmall } from 'central-icons/IconPlusSmall';
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import { ConfirmDialog, IconButton, RevelioTooltip } from '@revelio/ui';

import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../PageConfigProvider/constants';
import { usePageConfig } from '../PageConfigProvider/PageConfigProvider';
import type { Tab } from '../PageConfigProvider/types';
import { usePageState } from '../PageState/PageStateProvider';
import { resolveActiveTab } from '../PageState/resolveActiveTab';
import styles from './EditableTabManager.module.css';
import { useGetTabs, useSyncResolvedActiveTab, useUpdateTab } from './useTabs';

const MAX_TAB_LABEL_LENGTH = 40;

const nextTabLabel = (tabs: Tab[]) => {
  const taken = new Set(tabs.map((tab) => tab.label));
  let n = tabs.length + 1;
  while (taken.has(`Tab ${n}`)) n += 1;
  return `Tab ${n}`;
};

const createBlankTab = (tabs: Tab[]): Tab => {
  const primaryFilterId = tabs[0]?.tabContent.primaryFilter?.id;
  return {
    id: crypto.randomUUID(),
    label: nextTabLabel(tabs),
    isActive: false,
    tabContent: {
      id: crypto.randomUUID(),
      items: [],
      ...(primaryFilterId && { primaryFilter: { id: primaryFilterId } }),
      grid: { rows: DEFAULT_GRID_ROWS, columns: DEFAULT_GRID_COLUMNS },
      secondaryFilters: [],
    },
  };
};

export const EditableTabManager = () => {
  const tabs = useGetTabs();
  const { dispatch } = usePageConfig();
  const { pageState } = usePageState();
  const selectTab = useUpdateTab();

  useSyncResolvedActiveTab();

  const [editingTabId, setEditingTabId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Tab | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  const activeTabId = resolveActiveTab(tabs, pageState.activeTabId)?.id;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } })
  );

  const handleAddTab = () => {
    const tab = createBlankTab(tabs);
    dispatch({ type: 'ADD_TAB', payload: tab });
    selectTab(tab.id);
  };

  const handleRename = (tabId: string, label: string) => {
    setEditingTabId(null);
    dispatch({ type: 'RENAME_TAB', payload: { tabId, label } });
  };

  const handleRequestDelete = (tab: Tab) => {
    setDeleteTarget(tab);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    setIsDeleteDialogOpen(false);
    if (!deleteTarget) return;
    if (deleteTarget.id === activeTabId) {
      const index = tabs.findIndex((tab) => tab.id === deleteTarget.id);
      const neighbour = tabs[index - 1] ?? tabs[index + 1];
      if (neighbour) selectTab(neighbour.id);
    }
    dispatch({ type: 'DELETE_TAB', payload: { tabId: deleteTarget.id } });
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const tabIds = tabs.map((tab) => tab.id);
    const from = tabIds.indexOf(String(active.id));
    const to = tabIds.indexOf(String(over.id));
    if (from === -1 || to === -1) return;
    dispatch({
      type: 'REORDER_TABS',
      payload: { tabIds: arrayMove(tabIds, from, to) },
    });
  };

  return (
    <div className={styles.tabManager} data-testid="page-tabs-editor">
      {tabs.length > 1 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={tabs.map((tab) => tab.id)}
            strategy={horizontalListSortingStrategy}
          >
            <div role="tablist" className={styles.tabList}>
              {tabs.map((tab) => (
                <SortableTab
                  key={tab.id}
                  tab={tab}
                  isActive={tab.id === activeTabId}
                  isEditing={tab.id === editingTabId}
                  onSelect={() => selectTab(tab.id)}
                  onStartRename={() => setEditingTabId(tab.id)}
                  onRename={(label) => handleRename(tab.id, label)}
                  onCancelRename={() => setEditingTabId(null)}
                  onDelete={() => handleRequestDelete(tab)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <RevelioTooltip label="Add tab">
        <IconButton
          aria-label="Add tab"
          icon={<IconPlusSmall size={16} />}
          variant="ghost"
          size="sm"
          shape="squared"
          onClick={handleAddTab}
          data-testid="add-page-tab"
        />
      </RevelioTooltip>
      <ConfirmDialog
        open={isDeleteDialogOpen}
        title={`Delete "${deleteTarget?.label ?? ''}"?`}
        confirmLabel="Delete Tab"
        destructive
        onConfirm={handleConfirmDelete}
        onCancel={() => setIsDeleteDialogOpen(false)}
        data-testid="delete-page-tab-dialog"
      >
        All content on this tab will be lost.
      </ConfirmDialog>
    </div>
  );
};

type SortableTabProps = {
  tab: Tab;
  isActive: boolean;
  isEditing: boolean;
  onSelect: () => void;
  onStartRename: () => void;
  onRename: (label: string) => void;
  onCancelRename: () => void;
  onDelete: () => void;
};

const SortableTab = ({
  tab,
  isActive,
  isEditing,
  onSelect,
  onStartRename,
  onRename,
  onCancelRename,
  onDelete,
}: SortableTabProps) => {
  const { listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: tab.id, disabled: isEditing });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      {...listeners}
      className={classNames(
        styles.tab,
        isActive && styles.tabActive,
        isDragging && styles.tabDragging
      )}
      data-testid="page-tab"
    >
      {isEditing ? (
        <TabLabelInput
          initialLabel={tab.label}
          onCommit={onRename}
          onCancel={onCancelRename}
        />
      ) : (
        <button
          type="button"
          role="tab"
          aria-selected={isActive}
          title={tab.label}
          className={styles.tabLabel}
          onClick={onSelect}
          onDoubleClick={onStartRename}
        >
          {tab.label}
        </button>
      )}
      <button
        type="button"
        aria-label={`Delete tab "${tab.label}"`}
        className={styles.deleteButton}
        onClick={onDelete}
        onPointerDown={(event) => event.stopPropagation()}
        data-testid="delete-page-tab"
      >
        <IconCrossSmall size={12} />
      </button>
    </div>
  );
};

const TabLabelInput = ({
  initialLabel,
  onCommit,
  onCancel,
}: {
  initialLabel: string;
  onCommit: (label: string) => void;
  onCancel: () => void;
}) => {
  const [value, setValue] = useState(initialLabel);
  const inputRef = useRef<HTMLInputElement>(null);
  const isCancelledRef = useRef(false);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  return (
    <input
      ref={inputRef}
      aria-label="Tab name"
      value={value}
      maxLength={MAX_TAB_LABEL_LENGTH}
      size={Math.max(value.length, 4)}
      className={styles.labelInput}
      onChange={(event) => setValue(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.currentTarget.blur();
        } else if (event.key === 'Escape') {
          isCancelledRef.current = true;
          onCancel();
        }
      }}
      onBlur={() => {
        if (!isCancelledRef.current) onCommit(value);
      }}
      data-testid="page-tab-name-input"
    />
  );
};
